import type { ChangeEvent, FormEvent, HTMLAttributes } from 'react';

import { PrimaryButton, Spinner, TextInput, classNames } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { getUserNameAndInitials } from '../../service/authentication/keycloak-provider';
import { getUserInformation } from '../../utils/user-information-utils';

import styles from './nickname-form.module.css';

type Props = {
  loading?: boolean;
  readOnly?: boolean;
  onSubmit: (nickname: string) => void;
} & Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'>;

export function NicknameForm({ loading, readOnly, onSubmit, className, ...props }: Props) {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();

  const [nickname, setNickname] = useState('');

  useEffect(() => {
    const viewerInformation = getUserInformation(webcastId);

    const nickname =
      getUserNameAndInitials().userName ||
      getUserNameAndInitials().email ||
      viewerInformation?.nickname ||
      viewerInformation?.email;

    if (nickname) setNickname(nickname);
  }, [webcastId]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNickname(event.target.value);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!nickname || loading) return;

    onSubmit(nickname);
  };

  return (
    <form
      data-testid="nickname-form"
      className={classNames(styles.nicknameForm, className)}
      onSubmit={handleSubmit}
      {...props}
    >
      {!readOnly && (
        <TextInput
          data-testid="nickname-input"
          placeholder={t('common.chat.joinChatInput.placeholder')}
          value={nickname}
          disabled={loading}
          onChange={handleChange}
        />
      )}

      <div className={styles.buttonWrapper}>
        {readOnly && <span data-testid="nickname-login-info">{t('common.chat.loggedInAs', { nickname })}</span>}

        <PrimaryButton data-testid="nickname-submit-button" type="submit" disabled={!nickname}>
          {loading ? <Spinner /> : t('common.chat.joinChatButton')}
        </PrimaryButton>
      </div>
    </form>
  );
}
